<script lang="ts">
	import DialogImageGallery from '$lib/components/Dialogs/DialogImageGallery.svelte';
	import { cn } from '$lib/utils.js';
	import { localizeHref } from '@/paraglide/runtime';
	import type {
		ListingsResponse,
		ReviewsResponse,
		UsersResponse
	} from '@/pocketbase/pocketbase-types';
	import { pb } from '@/utils/ApiClient';
	import Star from '@lucide/svelte/icons/star';

	interface Props {
		review: ReviewsResponse<{ reviewer: UsersResponse; listing: ListingsResponse }>;
		compact?: boolean;
	}

	let { review, compact = false }: Props = $props();

	let imageDialogOpen = $state(false);
	let initialImageIndex = $state(0);

	const reviewer = $derived(review.expand?.reviewer);
	const reviewerName = $derived(reviewer?.name || reviewer?.username || 'Usuario');
	const reviewDate = $derived(
		new Date(review.created).toLocaleDateString('es-ES', {
			year: 'numeric',
			month: 'long'
		})
	);
	const reviewerProfileHref = $derived(
		reviewer?.username ? localizeHref(`/perfil/${reviewer.username}`) : null
	);
	const reviewPhotos = $derived(review.photos ?? []);

	function openPhoto(index: number) {
		initialImageIndex = index;
		imageDialogOpen = true;
	}
</script>

<article
	class="flex flex-col gap-2 border-b border-gray-950/10 pb-8 last:border-b-0 last:pb-0"
	aria-labelledby="review-{review.id}-heading"
>
	<div class="flex items-start gap-3">
		{#if reviewer}
			{#if reviewerProfileHref}
				<a href={reviewerProfileHref} class="shrink-0">
					<img
						src={pb.files.getURL(reviewer, reviewer.avatar, { thumb: '80x80' })}
						alt="Perfil de {reviewerName}"
						class="size-10 rounded-full object-cover outline-1 -outline-offset-1 outline-black/5"
						width="40"
						height="40"
					/>
				</a>
			{:else}
				<img
					src={pb.files.getURL(reviewer, reviewer.avatar, { thumb: '80x80' })}
					alt="Perfil de {reviewerName}"
					class="size-10 shrink-0 rounded-full object-cover outline-1 -outline-offset-1 outline-black/5"
					width="40"
					height="40"
				/>
			{/if}
		{/if}

		<div class="min-w-0 flex-1">
			{#if reviewerProfileHref}
				<a
					id="review-{review.id}-heading"
					href={reviewerProfileHref}
					class="text-base/6 font-semibold text-gray-900 sm:text-sm/6"
				>
					{reviewerName}
				</a>
			{:else}
				<p
					id="review-{review.id}-heading"
					class="text-base/6 font-semibold text-gray-900 sm:text-sm/6"
				>
					{reviewerName}
				</p>
			{/if}
			{#if reviewer?.username && reviewer.username !== reviewerName}
				<p class="text-base/6 text-gray-500 sm:text-sm/6">@{reviewer.username}</p>
			{/if}
		</div>
	</div>

	<div class="flex flex-wrap items-center gap-x-1.5 gap-y-1">
		<span class="sr-only">Valoración {review.rating} de 5</span>
		<div class="flex items-center gap-0.5" aria-hidden="true">
			{#each [0, 1, 2, 3, 4] as i (i)}
				<Star
					class={cn(
						'h-3.5 w-3.5 shrink-0',
						i < review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
					)}
				/>
			{/each}
		</div>
		<span class="text-gray-400" aria-hidden="true">·</span>
		<time datetime={review.created} class="text-base/6 text-gray-900 sm:text-sm/6"
			>{reviewDate}</time
		>
	</div>

	{#if review.comment}
		<p class="text-base/7 text-gray-900 sm:text-sm/6">{review.comment}</p>
	{/if}

	{#if !compact && reviewPhotos.length > 0}
		<div class="flex gap-2 overflow-x-auto pb-1">
			{#each reviewPhotos as photo, index (photo)}
				<button
					type="button"
					class="shrink-0 overflow-hidden rounded-lg focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:outline-none"
					onclick={() => openPhoto(index)}
					aria-label="Ver foto {index + 1} de {reviewPhotos.length} de la reseña"
				>
					<img
						src={pb.files.getURL(review, photo, { thumb: '0x150' })}
						alt=""
						class="size-20 object-cover"
						width="80"
						height="80"
					/>
				</button>
			{/each}
		</div>

		<DialogImageGallery
			bind:open={imageDialogOpen}
			photosList={reviewPhotos}
			record={review}
			initialImageIndex={initialImageIndex}
			alt="Foto de la reseña de {reviewerName}"
		/>
	{/if}
</article>
